import type {
  StaffCoverageGaps,
  StaffCustomerCoverage,
  StaffCustomerSupport,
  StaffTeam,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useTeamRuntime } from "./runtime";

// The reads of the team, coverage and support screens. A staff answer holds names of customers and of staff, so nothing stays in the cache
// after the page lets go (`gcTime: 0`) and nothing is written to browser storage. A failed read is not retried by the library: the page shows
// the error and offers a retry. The reads are read again when a page opens (`refetchOnMount: "always"`), so a screen never shows the state
// from before a command.
//
// The directory and the gaps are paged by the server (CHT-768 review fix 6): the hooks load the first page and `fetchNextPage` loads the next
// with the `nextCursor` of the last page. `data` merges the pages loaded so far, so a page of the admin reads one list; `hasNextPage` says that
// more is on the server, and the screen shows a "Load more" control instead of a list that looks complete.

export const teamKeys = {
  all: ["staff-team"] as const,
  team: ["staff-team", "team"] as const,
  gaps: ["staff-team", "gaps"] as const,
  coverage: (customerId: string) => ["staff-team", "coverage", customerId] as const,
  support: (customerId: string) => ["staff-team", "support", customerId] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: "always",
} as const;

/** The pages loaded so far as one answer: the viewer, the summary and the time of the last page, every member in order. */
function mergeTeam(pages: readonly StaffTeam[]): StaffTeam {
  const last = pages[pages.length - 1] as StaffTeam;
  return { ...last, members: pages.flatMap((page) => page.members) };
}

function mergeGaps(pages: readonly StaffCoverageGaps[]): StaffCoverageGaps {
  const last = pages[pages.length - 1] as StaffCoverageGaps;
  return { ...last, gaps: pages.flatMap((page) => page.gaps) };
}

export function useStaffTeam(enabled: boolean) {
  const { api } = useTeamRuntime();
  return useInfiniteQuery({
    queryKey: teamKeys.team,
    queryFn: ({ signal, pageParam }) =>
      api.team(pageParam === null ? undefined : { cursor: pageParam }, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: StaffTeam) => last.nextCursor,
    select: (data) => mergeTeam(data.pages),
    enabled,
    ...READ_OPTIONS,
  });
}

export function useCoverageGaps(enabled: boolean) {
  const { api } = useTeamRuntime();
  return useInfiniteQuery({
    queryKey: teamKeys.gaps,
    queryFn: ({ signal, pageParam }) =>
      api.coverageGaps(pageParam === null ? undefined : { cursor: pageParam }, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: StaffCoverageGaps) => last.nextCursor,
    select: (data) => mergeGaps(data.pages),
    enabled,
    ...READ_OPTIONS,
  });
}

export function useCustomerCoverage(customerId: string, enabled: boolean) {
  const { api } = useTeamRuntime();
  return useQuery<StaffCustomerCoverage>({
    queryKey: teamKeys.coverage(customerId),
    queryFn: ({ signal }) => api.customerCoverage(customerId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

export function useCustomerSupport(customerId: string, enabled: boolean) {
  const { api } = useTeamRuntime();
  return useQuery<StaffCustomerSupport>({
    queryKey: teamKeys.support(customerId),
    queryFn: ({ signal }) => api.customerSupport(customerId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}
